import {announce} from '@github-ui/aria-live'
import {createContext, useCallback, use, useEffect, useMemo, useRef, useState} from 'react'

export type SubIssueStateContext = {
  createDialogOpen: boolean
  activeIssueId: string | undefined
  closeCreateDialog: () => void
  openCreateDialog: (issueId: string) => void
}

export const SUB_ISSUE_FEEDBACK = {
  created: 'Sub-issue created',
  added: 'Sub-issue added',
} as const

/** A confirmation shown beside the sub-issue buttons of the parent issue it applies to. */
export type SubIssueFeedback = {
  parentIssueId: string
  message: string
  /** Changes with every confirmation, so the same message reported again still confirms it. */
  id: number
  /** Whether the message should take focus once it renders, which is how screen readers hear it. */
  takeFocus: boolean
}

export type SubIssueFeedbackContext = {
  feedback: SubIssueFeedback | null
  clearFeedback: () => void
  /**
   * Confirms a created sub-issue. Pass the create dialog session the sub-issue was submitted from
   * (see `getCreateDialogSession`) so a later dialog isn't mistaken for the one that submitted it.
   */
  reportSubIssueCreated: (parentIssueId: string, fromCreateDialogSession?: number) => void
  /** Identifies the current create dialog, which changes each time one opens. */
  getCreateDialogSession: () => number
  reportSubIssueAdded: (parentIssueId: string) => void
  /** Whether focus is still where the create dialog left it, so moving it to the confirmation won't pull someone away. */
  canFocusFeedback: () => boolean
}

let nextFeedbackId = 0

/**
 * Context sub-issues-related state in the issue viewer.
 */
export const SubIssueStateContext = createContext<SubIssueStateContext | undefined>(undefined)

/** Confirmations for sub-issue changes, kept apart so dialog state changes don't re-render their readers. */
export const SubIssueFeedbackContext = createContext<SubIssueFeedbackContext | undefined>(undefined)

export function SubIssueStateProvider({children}: {children: React.ReactNode}) {
  const [activeIssueId, setActiveIssueId] = useState<string | undefined>(undefined)
  const [feedback, setFeedback] = useState<SubIssueFeedback | null>(null)
  const activeIssueIdRef = useRef(activeIssueId)
  // Where focus landed once the create dialog closed. The request finishes later, and focus only
  // moves to the confirmation if it is still there, so it never pulls someone away from where they went next.
  const focusAfterDialogCloseRef = useRef<Element | null>(null)
  const focusWhenCreatedRef = useRef<Element | null>(null)
  useEffect(() => {
    activeIssueIdRef.current = activeIssueId
    if (activeIssueId !== undefined) {
      focusAfterDialogCloseRef.current = null
      return
    }
    // Wait a frame so the dialog has handed focus back first.
    const frame = requestAnimationFrame(() => {
      focusAfterDialogCloseRef.current = document.activeElement
    })
    return () => cancelAnimationFrame(frame)
  }, [activeIssueId])

  const createDialogOpen = activeIssueId !== undefined

  const clearFeedback = useCallback(() => setFeedback(null), [])

  const createDialogSessionRef = useRef(0)
  const getCreateDialogSession = useCallback(() => createDialogSessionRef.current, [])

  const openCreateDialog = useCallback(
    (issueId: string) => {
      clearFeedback()
      createDialogSessionRef.current += 1
      setActiveIssueId(issueId)
    },
    [clearFeedback],
  )
  const closeCreateDialog = useCallback(() => setActiveIssueId(undefined), [])

  const reportSubIssueCreated = useCallback((parentIssueId: string, fromCreateDialogSession?: number) => {
    const message = SUB_ISSUE_FEEDBACK.created
    if (activeIssueIdRef.current !== undefined) {
      setFeedback({parentIssueId, message, id: nextFeedbackId++, takeFocus: false})
      // With "Create more" the dialog that submitted it stays open and confirms each sub-issue itself.
      // A dialog opened since then can't, so announce without moving focus out of it.
      const submittedFromOpenDialog =
        fromCreateDialogSession === undefined || fromCreateDialogSession === createDialogSessionRef.current
      if (!submittedFromOpenDialog) announce(message)
      return
    }

    focusWhenCreatedRef.current = focusAfterDialogCloseRef.current ?? document.activeElement
    setFeedback({parentIssueId, message, id: nextFeedbackId++, takeFocus: true})
  }, [])

  const canFocusFeedback = useCallback(() => {
    const active = document.activeElement
    return !active || active === document.body || active === focusWhenCreatedRef.current
  }, [])

  const reportSubIssueAdded = useCallback((parentIssueId: string) => {
    setFeedback({parentIssueId, message: SUB_ISSUE_FEEDBACK.added, id: nextFeedbackId++, takeFocus: false})
    announce(SUB_ISSUE_FEEDBACK.added)
  }, [])

  const stateValue = useMemo(
    () => ({createDialogOpen, activeIssueId, closeCreateDialog, openCreateDialog}),
    [activeIssueId, closeCreateDialog, createDialogOpen, openCreateDialog],
  )

  const feedbackValue = useMemo(
    () => ({
      feedback,
      clearFeedback,
      reportSubIssueCreated,
      getCreateDialogSession,
      reportSubIssueAdded,
      canFocusFeedback,
    }),
    [canFocusFeedback, clearFeedback, feedback, getCreateDialogSession, reportSubIssueAdded, reportSubIssueCreated],
  )

  return (
    <SubIssueStateContext value={stateValue}>
      <SubIssueFeedbackContext value={feedbackValue}>{children}</SubIssueFeedbackContext>
    </SubIssueStateContext>
  )
}

export const useSubIssueState = (): SubIssueStateContext => {
  const context = use(SubIssueStateContext)
  if (!context) {
    throw new Error('useSubIssueState must be used within a SubIssueStateContext')
  }
  return context
}

export const useSubIssueFeedback = (): SubIssueFeedbackContext => {
  const context = use(SubIssueFeedbackContext)
  if (!context) {
    throw new Error('useSubIssueFeedback must be used within a SubIssueStateProvider')
  }
  return context
}

/** For components that also render outside the issue viewer, where there is no provider. */
export const useOptionalSubIssueFeedback = (): SubIssueFeedbackContext | undefined => use(SubIssueFeedbackContext)
